import { Component, ErrorInfo, ReactNode } from 'react';
import { Link as RouterLink, useRouteError } from 'react-router-dom';
import { Box, Button, Card, Stack, Typography } from '@mui/material';
import ErrorOutlineTwoToneIcon from '@mui/icons-material/ErrorOutlineTwoTone';
import { useTranslation } from 'react-i18next';

interface RouteErrorFallbackProps {
  error?: unknown;
}

const errorText = (error: unknown): string | null => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return null;
};

/** What a crashed page shows instead of a blank screen. */
export function RouteErrorFallback({ error }: RouteErrorFallbackProps) {
  const { t } = useTranslation();
  const detail = errorText(error);

  return (
    <Box
      role="alert"
      sx={{ display: 'flex', justifyContent: 'center', px: 2, py: 6 }}
    >
      <Card sx={{ maxWidth: 520, width: '100%', p: 4, textAlign: 'center' }}>
        <ErrorOutlineTwoToneIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h4" gutterBottom>
          {t('errorBoundary.title')}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t('errorBoundary.body')}
        </Typography>
        <Stack direction="row" spacing={1.5} justifyContent="center">
          <Button variant="contained" onClick={() => window.location.reload()}>
            {t('errorBoundary.reload')}
          </Button>
          <Button variant="outlined" component={RouterLink} to="/">
            {t('errorBoundary.home')}
          </Button>
        </Stack>
        {detail && (
          <Box
            component="details"
            sx={{ mt: 3, textAlign: 'left', color: 'text.secondary' }}
          >
            <summary>{t('errorBoundary.details')}</summary>
            <Typography
              component="pre"
              variant="body2"
              sx={{ mt: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {detail}
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
}

interface RouteErrorBoundaryProps {
  children: ReactNode;
}

interface RouteErrorBoundaryState {
  error: unknown;
  hasError: boolean;
}

/**
 * Catches a render crash in the page area so the sidebar and header stay
 * usable. Give it `key={pathname}` so moving to another page starts clean.
 */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { error: null, hasError: false };

  static getDerivedStateFromError(error: unknown): RouteErrorBoundaryState {
    return { error, hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('Page crashed', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return <RouteErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

/** Router `errorElement`: a crash outside the page area (layout, guards). */
export function RouteErrorPage() {
  const error = useRouteError();
  return <RouteErrorFallback error={error} />;
}
